import { GetStaticProps } from "next";
import { NextSeo } from "next-seo";
import { useForm } from "react-hook-form";

import Layout from "../components/Layout";
import SimpleCenteredHeader from "../components/sections/SimpleCenteredHeader";
import classNames from "../lib/classNames";
import getCommonProps, { CommonProps } from "../lib/getCommonProps";

type FormData = {
  name: string;
  email: string;
  country: string;
  streetAddress: string;
  city: string;
  region: string;
  zipCode: string;
  nationality: string;
  age: number;
  gender: string;
  industry: string;
  jobPosition: string;
  weight: number;
  height: number;
  howToFind: string;
  medicalTreatment: string;
  medicalTreatmentDetails: string;
  prescribedMedicines: string;
  prescribedMedicinesDetails: string;
  allergic: string;
  allergicDetails: string;
  medicalWarningCard: string;
  pregnant: string;
  hayFever: string;
  eczema: string;
  chestCondition: string;
  chestConditionDetails: string;
  faintingAttacks: string;
  faintingAttacksDetails: string;
  heartProblems: string;
  heartProblemsDetails: string;
  diabetes: string;
  diabetesFamily: string;
  diabetesFamilyDetails: string;
  bruising: string;
  bruisingDetails: string;
  infected: string;
  infectedDetails: string;
  seriousIllness: string;
  seriousIllnessDetails: string;
  hospital: string;
  hospitalDetails: string;
  arthritis: string;
  rheumaticFever: string;
  chorea: string;
  bloodRefused: string;
  badLocalAnaesthetic: string;
  badGeneralAnaesthetic: string;
  heartSurgery: string;
  smoke: string;
  chewTobaacco: string;
  drinkAlcohol: string;
};

type Props = CommonProps;

const MedicalQuestionnaire = ({ ...commonProps }: Props) => {
  const {
    register,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<FormData>();

  return (
    <Layout {...commonProps}>
      <NextSeo title="Medical Questionnaire" noindex={true} />
      <SimpleCenteredHeader
        superTitle={"Medical Questionnaire"}
        title={"Medical Questionnaire"}
      />
      <div className="max-w-5xl px-3 mx-auto">
        <form
          className="space-y-8 divide-y divide-gray-200"
          action="https://submit-form.com/OGNeG9Hf"
        >
          <div className="space-y-8 divide-y divide-gray-200">
            <div className="pt-8">
              <div>
                <h3 className="text-lg font-medium leading-6 text-gray-900">
                  Personal Information
                </h3>
              </div>
              <div className="grid grid-cols-1 mt-6 gap-y-6 gap-x-4 sm:grid-cols-6">
                <div className="sm:col-span-6">
                  <label
                    htmlFor="name"
                    className="block text-sm font-medium text-gray-700"
                  >
                    Name
                  </label>
                  <div className="mt-1">
                    <input
                      type="text"
                      id="name"
                      autoComplete="given-name"
                      className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary sm:text-sm"
                      {...register("name", { required: true })}
                    />
                  </div>
                </div>
                <div className="sm:col-span-4">
                  <label
                    htmlFor="email"
                    className="block text-sm font-medium text-gray-700"
                  >
                    Email address
                  </label>
                  <div className="mt-1">
                    <input
                      id="email"
                      type="email"
                      autoComplete="email"
                      className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary sm:text-sm"
                      {...register("email", { required: true })}
                    />
                  </div>
                </div>
                <div className="sm:col-span-3">
                  <label
                    htmlFor="country"
                    className="block text-sm font-medium text-gray-700"
                  >
                    Country
                  </label>
                  <div className="mt-1">
                    <select
                      id="country"
                      autoComplete="country-name"
                      className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary sm:text-sm"
                      {...register("country", { required: true })}
                    >
                      <option>United States</option>
                      <option>Canada</option>
                      <option>Mexico</option>
                    </select>
                  </div>
                </div>
                <div className="sm:col-span-6">
                  <label
                    htmlFor="street-address"
                    className="block text-sm font-medium text-gray-700"
                  >
                    Street address
                  </label>
                  <div className="mt-1">
                    <input
                      type="text"
                      id="street-address"
                      autoComplete="street-address"
                      className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary sm:text-sm"
                      {...register("streetAddress", { required: true })}
                    />
                  </div>
                </div>
                <div className="sm:col-span-2">
                  <label
                    htmlFor="city"
                    className="block text-sm font-medium text-gray-700"
                  >
                    City
                  </label>
                  <div className="mt-1">
                    <input
                      type="text"
                      id="city"
                      autoComplete="address-level2"
                      className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary sm:text-sm"
                      {...register("city")}
                    />
                  </div>
                </div>
                <div className="sm:col-span-2">
                  <label
                    htmlFor="region"
                    className="block text-sm font-medium text-gray-700"
                  >
                    State / Province
                  </label>
                  <div className="mt-1">
                    <input
                      type="text"
                      id="region"
                      autoComplete="address-level1"
                      className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary sm:text-sm"
                      {...register("region")}
                    />
                  </div>
                </div>
                <div className="sm:col-span-2">
                  <label
                    htmlFor="postal-code"
                    className="block text-sm font-medium text-gray-700"
                  >
                    ZIP / Postal code
                  </label>
                  <div className="mt-1">
                    <input
                      type="text"
                      id="postal-code"
                      autoComplete="postal-code"
                      className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary sm:text-sm"
                      {...register("zipCode")}
                    />
                  </div>
                </div>
                <div className="sm:col-span-2">
                  <label
                    htmlFor="nationality"
                    className="block text-sm font-medium text-gray-700"
                  >
                    Nationality
                  </label>
                  <div className="mt-1">
                    <input
                      type="text"
                      id="nationality"
                      autoComplete="nationality"
                      className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary sm:text-sm"
                      {...register("nationality")}
                    />
                  </div>
                </div>
                <div className="sm:col-span-2">
                  <label
                    htmlFor="postal-code"
                    className="block text-sm font-medium text-gray-700"
                  >
                    Age
                  </label>
                  <div className="mt-1">
                    <input
                      type="number"
                      id="age"
                      autoComplete="age"
                      className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary sm:text-sm"
                      {...register("age")}
                    />
                  </div>
                </div>
                <div className="sm:col-span-2">
                  <label
                    htmlFor="gender"
                    className="block text-sm font-medium text-gray-700"
                  >
                    Gender
                  </label>
                  <div className="flex items-center mt-3 space-x-4">
                    <input
                      type="radio"
                      id="male"
                      className="w-4 h-4 border-gray-300 text-primary focus:ring-primary"
                      defaultValue="male"
                      {...register("gender")}
                    />
                    <label
                      htmlFor="male"
                      className="block ml-3 text-sm font-medium text-gray-700"
                    >
                      Male
                    </label>
                    <input
                      type="radio"
                      id="female"
                      className="w-4 h-4 border-gray-300 text-primary focus:ring-primary"
                      defaultValue="female"
                      {...register("gender")}
                    />
                    <label
                      htmlFor="female"
                      className="block ml-3 text-sm font-medium text-gray-700"
                    >
                      Female
                    </label>
                  </div>
                </div>
                <div className="sm:col-span-3">
                  <label
                    htmlFor="industry"
                    className="block text-sm font-medium text-gray-700"
                  >
                    Industry
                  </label>
                  <div className="mt-1">
                    <input
                      type="text"
                      id="industry"
                      autoComplete="industry"
                      className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary sm:text-sm"
                      {...register("industry")}
                    />
                  </div>
                </div>
                <div className="sm:col-span-3">
                  <label
                    htmlFor="jobPosition"
                    className="block text-sm font-medium text-gray-700"
                  >
                    Job Position
                  </label>
                  <div className="mt-1">
                    <input
                      type="text"
                      id="jobPosition"
                      autoComplete="job-position"
                      className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary sm:text-sm"
                      {...register("jobPosition")}
                    />
                  </div>
                </div>
                <div className="sm:col-span-2">
                  <label
                    htmlFor="height"
                    className="block text-sm font-medium text-gray-700"
                  >
                    What is your height in cm?
                  </label>
                  <div className="mt-1">
                    <input
                      type="number"
                      id="height"
                      autoComplete="height"
                      className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary sm:text-sm"
                      {...register("height")}
                    />
                  </div>
                </div>
                <div className="sm:col-span-2">
                  <label
                    htmlFor="weight"
                    className="block text-sm font-medium text-gray-700"
                  >
                    What is your weight in Kg?
                  </label>
                  <div className="mt-1">
                    <input
                      type="number"
                      id="weight"
                      autoComplete="weight"
                      className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary sm:text-sm"
                      {...register("weight")}
                    />
                  </div>
                </div>
                <div className="sm:col-span-2">
                  <label
                    htmlFor="howToFind"
                    className="block text-sm font-medium text-gray-700"
                  >
                    How did you find out about us?
                  </label>
                  <div className="mt-1">
                    <input
                      type="text"
                      id="howToFind"
                      autoComplete="howToFind"
                      className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary sm:text-sm"
                      {...register("howToFind")}
                    />
                  </div>
                </div>
              </div>
            </div>
            <div className="pt-8">
              <div>
                <h3 className="text-lg font-medium leading-6 text-gray-900">
                  Your health status
                </h3>
                <p className="mt-1 text-sm text-gray-500">
                  We&apos;ll always let you know about important changes, but
                  you pick what else you want to hear about.
                </p>
              </div>
              <div className="mt-6 space-y-5">
                <fieldset>
                  <legend className="text-base font-medium text-gray-900">
                    Are You Currently Receiving Any Medical Treatment From A
                    Doctor/Hospital/Clinic?
                  </legend>
                  <div className="mt-4 space-y-4">
                    <div className="flex items-center mt-3 space-x-4">
                      <input
                        type="radio"
                        id="medicalTreatmentYes"
                        className="w-4 h-4 border-gray-300 text-primary focus:ring-primary"
                        defaultValue="yes"
                        {...register("medicalTreatment")}
                      />
                      <label
                        htmlFor="medicalTreatmentYes"
                        className="block ml-3 text-sm font-medium text-gray-700"
                      >
                        Yes
                      </label>
                      <input
                        type="radio"
                        id="medicalTreatmentNo"
                        className="w-4 h-4 border-gray-300 text-primary focus:ring-primary"
                        defaultValue="no"
                        {...register("medicalTreatment")}
                      />
                      <label
                        htmlFor="medicalTreatmentNo"
                        className="block ml-3 text-sm font-medium text-gray-700"
                      >
                        No
                      </label>
                    </div>
                    {watch("medicalTreatment") === "yes" && (
                      <div className="sm:col-span-6">
                        <label
                          htmlFor="medicalTreatmentDetails"
                          className="block text-sm font-medium text-gray-700"
                        >
                          About
                        </label>
                        <div className="mt-1">
                          <textarea
                            id="medicalTreatmentDetails"
                            rows={3}
                            className="block w-full border border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary sm:text-sm"
                            defaultValue={""}
                            placeholder="Please provide details"
                            {...register("medicalTreatmentDetails")}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </fieldset>
                <fieldset>
                  <legend className="text-base font-medium text-gray-900">
                    Are You Taking Any Prescribed Medicines?
                  </legend>
                  <div className="mt-4 space-y-4">
                    <div className="flex items-center mt-3 space-x-4">
                      <input
                        type="radio"
                        id="prescribedMedicinesYes"
                        className="w-4 h-4 border-gray-300 text-primary focus:ring-primary"
                        defaultValue="yes"
                        {...register("prescribedMedicines")}
                      />
                      <label
                        htmlFor="prescribedMedicinesYes"
                        className="block ml-3 text-sm font-medium text-gray-700"
                      >
                        Yes
                      </label>
                      <input
                        type="radio"
                        id="prescribedMedicinesNo"
                        className="w-4 h-4 border-gray-300 text-primary focus:ring-primary"
                        defaultValue="no"
                        {...register("prescribedMedicines")}
                      />
                      <label
                        htmlFor="prescribedMedicinesNo"
                        className="block ml-3 text-sm font-medium text-gray-700"
                      >
                        No
                      </label>
                    </div>
                    {watch("prescribedMedicines") === "yes" && (
                      <div className="sm:col-span-6">
                        <label
                          htmlFor="prescribedMedicinesDetails"
                          className="block text-sm font-medium text-gray-700"
                        >
                          About
                        </label>
                        <div className="mt-1">
                          <textarea
                            id="prescribedMedicinesDetails"
                            rows={3}
                            className="block w-full border border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary sm:text-sm"
                            defaultValue={""}
                            placeholder="Please provide details"
                            {...register("prescribedMedicinesDetails")}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </fieldset>
                <fieldset>
                  <legend className="text-base font-medium text-gray-900">
                    Are You Allergic To Any Food, Medicines Or Substances? (e.g.
                    Latex Or Penicillin...)
                  </legend>
                  <div className="mt-4 space-y-4">
                    <div className="flex items-center mt-3 space-x-4">
                      <input
                        type="radio"
                        id="allergicYes"
                        className="w-4 h-4 border-gray-300 text-primary focus:ring-primary"
                        defaultValue="yes"
                        {...register("allergic")}
                      />
                      <label
                        htmlFor="allergicYes"
                        className="block ml-3 text-sm font-medium text-gray-700"
                      >
                        Yes
                      </label>
                      <input
                        type="radio"
                        id="allergicNo"
                        className="w-4 h-4 border-gray-300 text-primary focus:ring-primary"
                        defaultValue="no"
                        {...register("allergic")}
                      />
                      <label
                        htmlFor="allergicNo"
                        className="block ml-3 text-sm font-medium text-gray-700"
                      >
                        No
                      </label>
                    </div>
                    {watch("allergic") === "yes" && (
                      <div className="sm:col-span-6">
                        <label
                          htmlFor="allergicDetails"
                          className="block text-sm font-medium text-gray-700"
                        >
                          About
                        </label>
                        <div className="mt-1">
                          <textarea
                            id="allergicDetails"
                            rows={3}
                            className="block w-full border border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary sm:text-sm"
                            defaultValue={""}
                            placeholder="Please provide details"
                            {...register("allergicDetails")}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </fieldset>
                <fieldset>
                  <legend className="text-base font-medium text-gray-900">
                    Do You Have Bronchitis, Asthma Or Any Other Chest Condition?
                  </legend>
                  <div className="mt-4 space-y-4">
                    <div className="flex items-center mt-3 space-x-4">
                      <input
                        type="radio"
                        id="chestConditionYes"
                        className="w-4 h-4 border-gray-300 text-primary focus:ring-primary"
                        defaultValue="yes"
                        {...register("chestCondition")}
                      />
                      <label
                        htmlFor="chestConditionYes"
                        className="block ml-3 text-sm font-medium text-gray-700"
                      >
                        Yes
                      </label>
                      <input
                        type="radio"
                        id="chestConditionNo"
                        className="w-4 h-4 border-gray-300 text-primary focus:ring-primary"
                        defaultValue="no"
                        {...register("chestCondition")}
                      />
                      <label
                        htmlFor="chestConditionNo"
                        className="block ml-3 text-sm font-medium text-gray-700"
                      >
                        No
                      </label>
                    </div>
                    {watch("chestCondition") === "yes" && (
                      <div>
                        <label
                          htmlFor="allergicDetails"
                          className="block text-sm font-medium text-gray-700"
                        >
                          About
                        </label>
                        <div className="mt-1">
                          <textarea
                            id="chestConditionDetails"
                            rows={3}
                            className="block w-full border border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary sm:text-sm"
                            defaultValue={""}
                            placeholder="Please provide details"
                            {...register("chestConditionDetails")}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </fieldset>
                <fieldset>
                  <legend className="text-base font-medium text-gray-900">
                    Do You Suffer From Fainting Attacks, Giddiness, Blackouts Or
                    Epilepsy?
                  </legend>
                  <div className="mt-4 space-y-4">
                    <div className="flex items-center mt-3 space-x-4">
                      <input
                        type="radio"
                        id="faintingAttacksYes"
                        className="w-4 h-4 border-gray-300 text-primary focus:ring-primary"
                        defaultValue="yes"
                        {...register("faintingAttacks")}
                      />
                      <label
                        htmlFor="faintingAttacksYes"
                        className="block ml-3 text-sm font-medium text-gray-700"
                      >
                        Yes
                      </label>
                      <input
                        type="radio"
                        id="faintingAttacksNo"
                        className="w-4 h-4 border-gray-300 text-primary focus:ring-primary"
                        defaultValue="no"
                        {...register("faintingAttacks")}
                      />
                      <label
                        htmlFor="faintingAttacksNo"
                        className="block ml-3 text-sm font-medium text-gray-700"
                      >
                        No
                      </label>
                    </div>
                    {watch("faintingAttacks") === "yes" && (
                      <div>
                        <label
                          htmlFor="faintingAttacksDetails"
                          className="block text-sm font-medium text-gray-700"
                        >
                          About
                        </label>
                        <div className="mt-1">
                          <textarea
                            id="faintingAttacksDetails"
                            rows={3}
                            className="block w-full border border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary sm:text-sm"
                            defaultValue={""}
                            placeholder="Please provide details"
                            {...register("faintingAttacksDetails")}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </fieldset>
                <fieldset>
                  <legend className="text-base font-medium text-gray-900">
                    Do You Have Heart Problems, Angina, Blood Pressure Problems
                    Or Stroke?
                  </legend>
                  <div className="mt-4 space-y-4">
                    <div className="flex items-center mt-3 space-x-4">
                      <input
                        type="radio"
                        id="heartProblemsYes"
                        className="w-4 h-4 border-gray-300 text-primary focus:ring-primary"
                        defaultValue="yes"
                        {...register("heartProblems")}
                      />
                      <label
                        htmlFor="heartProblemsYes"
                        className="block ml-3 text-sm font-medium text-gray-700"
                      >
                        Yes
                      </label>
                      <input
                        type="radio"
                        id="heartProblemsNo"
                        className="w-4 h-4 border-gray-300 text-primary focus:ring-primary"
                        defaultValue="no"
                        {...register("heartProblems")}
                      />
                      <label
                        htmlFor="heartProblemsNo"
                        className="block ml-3 text-sm font-medium text-gray-700"
                      >
                        No
                      </label>
                    </div>
                    {watch("heartProblems") === "yes" && (
                      <div>
                        <label
                          htmlFor="heartProblemsDetails"
                          className="block text-sm font-medium text-gray-700"
                        >
                          About
                        </label>
                        <div className="mt-1">
                          <textarea
                            id="heartProblemsDetails"
                            rows={3}
                            className="block w-full border border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary sm:text-sm"
                            defaultValue={""}
                            placeholder="Please provide details"
                            {...register("heartProblemsDetails")}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </fieldset>
                <fieldset>
                  <legend className="text-base font-medium text-gray-900">
                    Diabetes
                  </legend>
                  <div className="grid grid-cols-1 mt-6 gap-y-6 gap-x-4 sm:grid-cols-6">
                    <div className="sm:col-span-2">
                      <label
                        htmlFor="diabetes"
                        className="block text-sm font-medium text-gray-700"
                      >
                        Do You Have Diabetes?
                      </label>
                      <div className="flex items-center mt-3 space-x-4">
                        <input
                          type="radio"
                          id="diabetesYes"
                          className="w-4 h-4 border-gray-300 text-primary focus:ring-primary"
                          defaultValue="yes"
                          {...register("diabetes")}
                        />
                        <label
                          htmlFor="diabetesYes"
                          className="block ml-3 text-sm font-medium text-gray-700"
                        >
                          Yes
                        </label>
                        <input
                          type="radio"
                          id="diabetesNo"
                          className="w-4 h-4 border-gray-300 text-primary focus:ring-primary"
                          defaultValue="no"
                          {...register("diabetes")}
                        />
                        <label
                          htmlFor="diabetesNo"
                          className="block ml-3 text-sm font-medium text-gray-700"
                        >
                          No
                        </label>
                      </div>
                    </div>
                    <div className="sm:col-span-2">
                      <label
                        htmlFor="diabetesFamily"
                        className="block text-sm font-medium text-gray-700"
                      >
                        Do Anybody In Your Family Have Diabetes?
                      </label>
                      <div className="flex items-center mt-3 space-x-4">
                        <input
                          type="radio"
                          id="diabetesFamilyYes"
                          className="w-4 h-4 border-gray-300 text-primary focus:ring-primary"
                          defaultValue="yes"
                          {...register("diabetesFamily")}
                        />
                        <label
                          htmlFor="diabetesFamilyYes"
                          className="block ml-3 text-sm font-medium text-gray-700"
                        >
                          Yes
                        </label>
                        <input
                          type="radio"
                          id="diabetesFamilyNo"
                          className="w-4 h-4 border-gray-300 text-primary focus:ring-primary"
                          defaultValue="no"
                          {...register("diabetesFamily")}
                        />
                        <label
                          htmlFor="diabetesFamilyNo"
                          className="block ml-3 text-sm font-medium text-gray-700"
                        >
                          No
                        </label>
                      </div>
                    </div>
                    {watch("diabetesFamily") === "yes" && (
                      <div className="sm:col-span-6">
                        <label
                          htmlFor="diabetesFamilyDetails"
                          className="block text-sm font-medium text-gray-700"
                        >
                          Which family members?
                        </label>
                        <div className="mt-1">
                          <textarea
                            id="diabetesFamilyDetails"
                            rows={3}
                            className="block w-full border border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary sm:text-sm"
                            defaultValue={""}
                            placeholder="Please provide details"
                            {...register("diabetesFamilyDetails")}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </fieldset>
                <fieldset>
                  <legend className="text-base font-medium text-gray-900">
                    Do You Have Bruising Or Persistent Bleeding Following
                    Injury, Tooth Extraction Or Surgery?
                  </legend>
                  <div className="mt-4 space-y-4">
                    <div className="flex items-center mt-3 space-x-4">
                      <input
                        type="radio"
                        id="bruisingYes"
                        className="w-4 h-4 border-gray-300 text-primary focus:ring-primary"
                        defaultValue="yes"
                        {...register("bruising")}
                      />
                      <label
                        htmlFor="bruisingYes"
                        className="block ml-3 text-sm font-medium text-gray-700"
                      >
                        Yes
                      </label>
                      <input
                        type="radio"
                        id="bruisingNo"
                        className="w-4 h-4 border-gray-300 text-primary focus:ring-primary"
                        defaultValue="no"
                        {...register("bruising")}
                      />
                      <label
                        htmlFor="bruisingNo"
                        className="block ml-3 text-sm font-medium text-gray-700"
                      >
                        No
                      </label>
                    </div>
                    {watch("bruising") === "yes" && (
                      <div>
                        <label
                          htmlFor="bruisingDetails"
                          className="block text-sm font-medium text-gray-700"
                        >
                          About
                        </label>
                        <div className="mt-1">
                          <textarea
                            id="bruisingDetails"
                            rows={3}
                            className="block w-full border border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary sm:text-sm"
                            defaultValue={""}
                            placeholder="Please provide details"
                            {...register("bruisingDetails")}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </fieldset>
                <fieldset>
                  <legend className="text-base font-medium text-gray-900">
                    Do You Have Any Infectious Diseases?
                  </legend>
                  <div className="mt-4 space-y-4">
                    <div className="flex items-center mt-3 space-x-4">
                      <input
                        type="radio"
                        id="infectedYes"
                        className="w-4 h-4 border-gray-300 text-primary focus:ring-primary"
                        defaultValue="yes"
                        {...register("infected")}
                      />
                      <label
                        htmlFor="infectedYes"
                        className="block ml-3 text-sm font-medium text-gray-700"
                      >
                        Yes
                      </label>
                      <input
                        type="radio"
                        id="infectedNo"
                        className="w-4 h-4 border-gray-300 text-primary focus:ring-primary"
                        defaultValue="no"
                        {...register("infected")}
                      />
                      <label
                        htmlFor="infectedNo"
                        className="block ml-3 text-sm font-medium text-gray-700"
                      >
                        No
                      </label>
                    </div>
                    {watch("infected") === "yes" && (
                      <div>
                        <label
                          htmlFor="infectedDetails"
                          className="block text-sm font-medium text-gray-700"
                        >
                          About
                        </label>
                        <div className="mt-1">
                          <textarea
                            id="infectedDetails"
                            rows={3}
                            className="block w-full border border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary sm:text-sm"
                            defaultValue={""}
                            placeholder="Please provide details"
                            {...register("infectedDetails")}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </fieldset>
                <fieldset>
                  <legend className="text-base font-medium text-gray-900">
                    Do You Have Any Other Serious Illness?
                  </legend>
                  <div className="mt-4 space-y-4">
                    <div className="flex items-center mt-3 space-x-4">
                      <input
                        type="radio"
                        id="seriousIllnessYes"
                        className="w-4 h-4 border-gray-300 text-primary focus:ring-primary"
                        defaultValue="yes"
                        {...register("seriousIllness")}
                      />
                      <label
                        htmlFor="seriousIllnessYes"
                        className="block ml-3 text-sm font-medium text-gray-700"
                      >
                        Yes
                      </label>
                      <input
                        type="radio"
                        id="seriousIllnessNo"
                        className="w-4 h-4 border-gray-300 text-primary focus:ring-primary"
                        defaultValue="no"
                        {...register("seriousIllness")}
                      />
                      <label
                        htmlFor="seriousIllnessNo"
                        className="block ml-3 text-sm font-medium text-gray-700"
                      >
                        No
                      </label>
                    </div>
                    {watch("seriousIllness") === "yes" && (
                      <div>
                        <label
                          htmlFor="seriousIllnessDetails"
                          className="block text-sm font-medium text-gray-700"
                        >
                          About
                        </label>
                        <div className="mt-1">
                          <textarea
                            id="seriousIllnessDetails"
                            rows={3}
                            className="block w-full border border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary sm:text-sm"
                            defaultValue={""}
                            placeholder="Please provide details"
                            {...register("seriousIllnessDetails")}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </fieldset>
                <fieldset>
                  <legend className="text-base font-medium text-gray-900">
                    Have You Had Treatment Which Required You To Be In Hospital?
                  </legend>
                  <div className="mt-4 space-y-4">
                    <div className="flex items-center mt-3 space-x-4">
                      <input
                        type="radio"
                        id="hospitalYes"
                        className="w-4 h-4 border-gray-300 text-primary focus:ring-primary"
                        defaultValue="yes"
                        {...register("hospital")}
                      />
                      <label
                        htmlFor="hospitalYes"
                        className="block ml-3 text-sm font-medium text-gray-700"
                      >
                        Yes
                      </label>
                      <input
                        type="radio"
                        id="hospitalNo"
                        className="w-4 h-4 border-gray-300 text-primary focus:ring-primary"
                        defaultValue="no"
                        {...register("hospital")}
                      />
                      <label
                        htmlFor="hospitalNo"
                        className="block ml-3 text-sm font-medium text-gray-700"
                      >
                        No
                      </label>
                    </div>
                    {watch("hospital") === "yes" && (
                      <div>
                        <label
                          htmlFor="hospitalDetails"
                          className="block text-sm font-medium text-gray-700"
                        >
                          About
                        </label>
                        <div className="mt-1">
                          <textarea
                            id="hospitalDetails"
                            rows={3}
                            className="block w-full border border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary sm:text-sm"
                            defaultValue={""}
                            placeholder="Please provide details"
                            {...register("hospitalDetails")}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </fieldset>
                <fieldset>
                  <legend className="text-base font-medium text-gray-900">
                    Other Medical Conditions
                  </legend>
                  <div className="grid grid-cols-1 mt-6 gap-y-6 gap-x-4 sm:grid-cols-6">
                    <div className="sm:col-span-2">
                      <label
                        htmlFor="medicalWarningCard"
                        className="block text-sm font-medium text-gray-700"
                      >
                        Do You Carry A Medical Warning Card?
                      </label>
                      <div className="flex items-center mt-3 space-x-4">
                        <input
                          type="radio"
                          id="medicalWarningCardYes"
                          className="w-4 h-4 border-gray-300 text-primary focus:ring-primary"
                          defaultValue="yes"
                          {...register("medicalWarningCard")}
                        />
                        <label
                          htmlFor="medicalWarningCardYes"
                          className="block ml-3 text-sm font-medium text-gray-700"
                        >
                          Yes
                        </label>
                        <input
                          type="radio"
                          id="medicalWarningCardNo"
                          className="w-4 h-4 border-gray-300 text-primary focus:ring-primary"
                          defaultValue="no"
                          {...register("medicalWarningCard")}
                        />
                        <label
                          htmlFor="medicalWarningCardNo"
                          className="block ml-3 text-sm font-medium text-gray-700"
                        >
                          No
                        </label>
                      </div>
                    </div>
                    <div className="sm:col-span-2">
                      <label
                        htmlFor="pregnant"
                        className="block text-sm font-medium text-gray-700"
                      >
                        Are You Pregnant?
                      </label>
                      <div className="flex items-center mt-3 space-x-4">
                        <input
                          type="radio"
                          id="pregnantYes"
                          className="w-4 h-4 border-gray-300 text-primary focus:ring-primary"
                          defaultValue="yes"
                          {...register("pregnant")}
                        />
                        <label
                          htmlFor="pregnantYes"
                          className="block ml-3 text-sm font-medium text-gray-700"
                        >
                          Yes
                        </label>
                        <input
                          type="radio"
                          id="pregnantNo"
                          className="w-4 h-4 border-gray-300 text-primary focus:ring-primary"
                          defaultValue="no"
                          {...register("pregnant")}
                        />
                        <label
                          htmlFor="pregnantNo"
                          className="block ml-3 text-sm font-medium text-gray-700"
                        >
                          No
                        </label>
                      </div>
                    </div>
                    <div className="sm:col-span-2">
                      <label
                        htmlFor="feverOrEgzama"
                        className="block text-sm font-medium text-gray-700"
                      >
                        Do You Have Hay Fever Or Eczema?
                      </label>
                      <div className="flex items-center h-5 mt-3 space-x-3">
                        <div className="flex items-center h-5">
                          <input
                            id="hayFever"
                            type="checkbox"
                            className="w-4 h-4 border-gray-300 rounded text-primary focus:ring-primary"
                            {...register("hayFever")}
                          />
                        </div>
                        <div className="ml-3 text-sm">
                          <label
                            htmlFor="hayFever"
                            className="font-medium text-gray-700"
                          >
                            Hay Fever
                          </label>
                        </div>
                        <div className="flex items-center h-5">
                          <input
                            id="eczema"
                            type="checkbox"
                            className="w-4 h-4 border-gray-300 rounded text-primary focus:ring-primary"
                            {...register("eczema")}
                          />
                        </div>
                        <div className="ml-3 text-sm">
                          <label
                            htmlFor="eczema"
                            className="font-medium text-gray-700"
                          >
                            Eczema
                          </label>
                        </div>
                      </div>
                    </div>
                    <div className="sm:col-span-2">
                      <label
                        htmlFor="arthritis"
                        className="block text-sm font-medium text-gray-700"
                      >
                        Do You Suffer From Arthritis?
                      </label>
                      <div className="flex items-center mt-3 space-x-4">
                        <input
                          type="radio"
                          id="arthritisYes"
                          className="w-4 h-4 border-gray-300 text-primary focus:ring-primary"
                          defaultValue="yes"
                          {...register("arthritis")}
                        />
                        <label
                          htmlFor="arthritisYes"
                          className="block ml-3 text-sm font-medium text-gray-700"
                        >
                          Yes
                        </label>
                        <input
                          type="radio"
                          id="arthritisNo"
                          className="w-4 h-4 border-gray-300 text-primary focus:ring-primary"
                          defaultValue="no"
                          {...register("arthritis")}
                        />
                        <label
                          htmlFor="arthritisNo"
                          className="block ml-3 text-sm font-medium text-gray-700"
                        >
                          No
                        </label>
                      </div>
                    </div>
                    <div className="sm:col-span-2">
                      <label className="block text-sm font-medium text-gray-700">
                        Do You Have Rheumatic Fever Or Chorea?
                      </label>
                      <div className="flex items-center h-5 mt-3 space-x-3">
                        <div className="flex items-center h-5">
                          <input
                            id="rheumaticFever"
                            type="checkbox"
                            className="w-4 h-4 border-gray-300 rounded text-primary focus:ring-primary"
                            {...register("rheumaticFever")}
                          />
                        </div>
                        <div className="ml-3 text-sm">
                          <label
                            htmlFor="rheumaticFever"
                            className="font-medium text-gray-700"
                          >
                            Rheumatic Fever
                          </label>
                        </div>
                        <div className="flex items-center h-5">
                          <input
                            id="chorea"
                            type="checkbox"
                            className="w-4 h-4 border-gray-300 rounded text-primary focus:ring-primary"
                            {...register("chorea")}
                          />
                        </div>
                        <div className="ml-3 text-sm">
                          <label
                            htmlFor="chorea"
                            className="font-medium text-gray-700"
                          >
                            Chorea
                          </label>
                        </div>
                      </div>
                    </div>
                    <div className="sm:col-span-2">
                      <label
                        htmlFor="arthritis"
                        className="block text-sm font-medium text-gray-700"
                      >
                        Has Your Blood Ever Been Refused By The Blood
                        Transfusion Service?
                      </label>
                      <div className="flex items-center mt-3 space-x-4">
                        <input
                          type="radio"
                          id="bloodRefusedYes"
                          className="w-4 h-4 border-gray-300 text-primary focus:ring-primary"
                          defaultValue="yes"
                          {...register("bloodRefused")}
                        />
                        <label
                          htmlFor="bloodRefusedYes"
                          className="block ml-3 text-sm font-medium text-gray-700"
                        >
                          Yes
                        </label>
                        <input
                          type="radio"
                          id="bloodRefusedNo"
                          className="w-4 h-4 border-gray-300 text-primary focus:ring-primary"
                          defaultValue="no"
                          {...register("bloodRefused")}
                        />
                        <label
                          htmlFor="bloodRefusedNo"
                          className="block ml-3 text-sm font-medium text-gray-700"
                        >
                          No
                        </label>
                      </div>
                    </div>
                    <div className="sm:col-span-2">
                      <label className="block text-sm font-medium text-gray-700">
                        Have You Ever Had A Bad Reaction To Local Or General
                        Anaesthetic?
                      </label>
                      <div className="flex items-center h-5 mt-3 space-x-3">
                        <div className="flex items-center h-5">
                          <input
                            id="badLocalAnaesthetic"
                            type="checkbox"
                            className="w-4 h-4 border-gray-300 rounded text-primary focus:ring-primary"
                            {...register("badLocalAnaesthetic")}
                          />
                        </div>
                        <div className="ml-3 text-sm">
                          <label
                            htmlFor="badLocalAnaesthetic"
                            className="font-medium text-gray-700"
                          >
                            Local
                          </label>
                        </div>
                        <div className="flex items-center h-5">
                          <input
                            id="badGeneralAnaesthetic"
                            type="checkbox"
                            className="w-4 h-4 border-gray-300 rounded text-primary focus:ring-primary"
                            {...register("badGeneralAnaesthetic")}
                          />
                        </div>
                        <div className="ml-3 text-sm">
                          <label
                            htmlFor="badGeneralAnaesthetic"
                            className="font-medium text-gray-700"
                          >
                            General
                          </label>
                        </div>
                      </div>
                    </div>
                    <div className="sm:col-span-2">
                      <label
                        htmlFor="heartSurgery"
                        className="block text-sm font-medium text-gray-700"
                      >
                        Have You Had Heart Surgery?
                      </label>
                      <div className="flex items-center mt-3 space-x-4">
                        <input
                          type="radio"
                          id="heartSurgeryYes"
                          className="w-4 h-4 border-gray-300 text-primary focus:ring-primary"
                          defaultValue="yes"
                          {...register("heartSurgery")}
                        />
                        <label
                          htmlFor="heartSurgeryYes"
                          className="block ml-3 text-sm font-medium text-gray-700"
                        >
                          Yes
                        </label>
                        <input
                          type="radio"
                          id="heartSurgeryNo"
                          className="w-4 h-4 border-gray-300 text-primary focus:ring-primary"
                          defaultValue="no"
                          {...register("heartSurgery")}
                        />
                        <label
                          htmlFor="heartSurgeryNo"
                          className="block ml-3 text-sm font-medium text-gray-700"
                        >
                          No
                        </label>
                      </div>
                    </div>
                    <div className="sm:col-span-2">
                      <label className="block text-sm font-medium text-gray-700">
                        Do You Smoke?
                      </label>
                      <div className="flex items-center mt-3 space-x-4">
                        <input
                          type="radio"
                          id="smokeYes"
                          className="w-4 h-4 border-gray-300 text-primary focus:ring-primary"
                          defaultValue="yes"
                          {...register("smoke")}
                        />
                        <label
                          htmlFor="smokeYes"
                          className="block ml-3 text-sm font-medium text-gray-700"
                        >
                          Yes
                        </label>
                        <input
                          type="radio"
                          id="smokeNo"
                          className="w-4 h-4 border-gray-300 text-primary focus:ring-primary"
                          defaultValue="no"
                          {...register("smoke")}
                        />
                        <label
                          htmlFor="smokeNo"
                          className="block ml-3 text-sm font-medium text-gray-700"
                        >
                          No
                        </label>
                      </div>
                    </div>
                    <div className="sm:col-span-2">
                      <label className="block text-sm font-medium text-gray-700">
                        Do You Chew Tobacco, Paan, Gutka Or Supari Now Or Have
                        You Done So In The Past?
                      </label>
                      <div className="flex items-center mt-3 space-x-4">
                        <input
                          type="radio"
                          id="chewTobaaccoYes"
                          className="w-4 h-4 border-gray-300 text-primary focus:ring-primary"
                          defaultValue="yes"
                          {...register("chewTobaacco")}
                        />
                        <label
                          htmlFor="chewTobaaccoYes"
                          className="block ml-3 text-sm font-medium text-gray-700"
                        >
                          Yes
                        </label>
                        <input
                          type="radio"
                          id="chewTobaaccoNo"
                          className="w-4 h-4 border-gray-300 text-primary focus:ring-primary"
                          defaultValue="no"
                          {...register("chewTobaacco")}
                        />
                        <label
                          htmlFor="chewTobaaccoNo"
                          className="block ml-3 text-sm font-medium text-gray-700"
                        >
                          No
                        </label>
                      </div>
                    </div>
                    <div className="sm:col-span-2">
                      <label className="block text-sm font-medium text-gray-700">
                        Do You Drink Alcohol?
                      </label>
                      <div className="flex items-center mt-3 space-x-4">
                        <input
                          type="radio"
                          id="drinkAlcoholYes"
                          className="w-4 h-4 border-gray-300 text-primary focus:ring-primary"
                          defaultValue="yes"
                          {...register("drinkAlcohol")}
                        />
                        <label
                          htmlFor="drinkAlcoholYes"
                          className="block ml-3 text-sm font-medium text-gray-700"
                        >
                          Yes
                        </label>
                        <input
                          type="radio"
                          id="drinkAlcoholNo"
                          className="w-4 h-4 border-gray-300 text-primary focus:ring-primary"
                          defaultValue="no"
                          {...register("drinkAlcohol")}
                        />
                        <label
                          htmlFor="drinkAlcoholNo"
                          className="block ml-3 text-sm font-medium text-gray-700"
                        >
                          No
                        </label>
                      </div>
                    </div>
                  </div>
                </fieldset>
              </div>
            </div>
          </div>
          <div className="pt-5">
            <div className="flex justify-end">
              <button
                type="submit"
                className={classNames(
                  isSubmitting
                    ? "bg-gray-500"
                    : "bg-secondary hover:bg-lime-500 focus:ring-2 focus:ring-offset-2 focus:ring-primary focus:outline-none",
                  "inline-flex items-center justify-center w-full px-6 py-3 mt-2 text-base font-medium text-white border border-transparent rounded-md shadow-sm sm:w-auto"
                )}
                disabled={isSubmitting}
              >
                Submit
              </button>
            </div>
          </div>
        </form>
      </div>
    </Layout>
  );
};

export const getStaticProps: GetStaticProps = async (context) => {
  const commonData = await getCommonProps(context);

  return {
    props: {
      ...commonData,
    },
  };
};

export default MedicalQuestionnaire;
